/**
 * @file FeedRowView.tsx
 * @description Одна строка ленты диалога по модели `feed-model.ts`
 * (chat-window-design.md §3): разделитель дня, линия «Новые сообщения»,
 * пузырь, неотправленное сообщение, служебная запись или свёрнутая группа
 * записей. Вид строки — реестром по `row.type`, а не каскадом в ленте.
 * Здесь же заготовка ленты на время загрузки.
 */

import type { ReactNode } from "react";

import { Skeleton } from "../ui/skeleton";
import { CHAT_TEXT } from "./chat-texts";
import type { FeedRow } from "./feed-model";
import { FeedMessageBubble } from "./FeedMessageBubble";
import { PendingBubble } from "./PendingBubble";
import { ServiceRecordRow } from "./ServiceRecordRow";

interface RowContext {
  wide: boolean;
  /** Запись, подсвеченная после перехода из «Истории диалога». */
  flashId: string | null;
  onRetry: (tempId: string) => void;
  onRemove: (tempId: string) => void;
  onExpandGroup: (key: string) => void;
}

type RowRenderers = { [K in FeedRow["type"]]: (row: Extract<FeedRow, { type: K }>, ctx: RowContext) => ReactNode };

const ROW_RENDERERS: RowRenderers = {
  day: (row) => (
    <div className="flex items-center gap-3 py-1 text-xs text-on-surface-variant">
      <span className="h-px flex-1 bg-outline-variant" aria-hidden />
      {row.title}
      <span className="h-px flex-1 bg-outline-variant" aria-hidden />
    </div>
  ),
  unreadLine: () => (
    <div data-chat-unread-line="" className="flex items-center gap-3 text-xs font-medium text-primary">
      <span className="h-px flex-1 bg-primary" aria-hidden />
      {CHAT_TEXT.newMessagesLine}
      <span className="h-px flex-1 bg-primary" aria-hidden />
    </div>
  ),
  message: (row, ctx) => <FeedMessageBubble message={row.message} side={row.side} showAuthor={row.showAuthor} wide={ctx.wide} />,
  pending: (row, ctx) => <PendingBubble pending={row.pending} wide={ctx.wide} onRetry={ctx.onRetry} onRemove={ctx.onRemove} />,
  event: (row, ctx) => <ServiceRecordRow event={row.event} highlight={row.highlight} flash={row.event.id === ctx.flashId} />,
  eventGroup: (row, ctx) => (
    <button
      type="button"
      onClick={() => ctx.onExpandGroup(row.key)}
      className="mx-auto min-h-8 rounded-full px-3 text-xs text-on-surface-variant outline-none hover:bg-surface-low focus-visible:ring-2 focus-visible:ring-ring"
    >
      · {CHAT_TEXT.serviceCollapsed(row.events.length)} ·
    </button>
  ),
};

export function FeedRowView({ row, ctx }: { row: FeedRow; ctx: RowContext }) {
  const render = ROW_RENDERERS[row.type] as (row: FeedRow, ctx: RowContext) => ReactNode;
  return <>{render(row, ctx)}</>;
}

export function FeedSkeleton() {
  return (
    <div aria-busy="true" aria-label="Загрузка переписки" className="flex flex-col gap-3 p-4">
      <Skeleton className="h-12 w-3/5 rounded-xl" />
      <Skeleton className="ml-auto h-10 w-1/2 rounded-xl" />
      <Skeleton className="h-16 w-2/3 rounded-xl" />
      <Skeleton className="ml-auto h-10 w-2/5 rounded-xl" />
    </div>
  );
}
